import React, { useCallback, useEffect, useMemo, useState } from "react";
import { ExternalLink, Newspaper, RefreshCw } from "lucide-react";
import { fetchMySleeperNews, fetchPublicSleeperNews, getUser } from "@/lib/api";
import { apiErrorMessage } from "@/lib/apiErrorMessage";
import { positionColor } from "@/lib/format";
import { SignInRequired } from "@/components/SignInRequired";
import { AdSlot } from "@/components/AdSlot";
import { getAdSenseSlot } from "@/lib/ads";

const NEWS_USER_KEY = "trystomatic.news.sleeperUsername";

function formatWhen(iso) {
  if (!iso) return null;
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return null;
    return d.toLocaleString(undefined, {
      month: "short",
      day: "numeric",
      hour: "numeric",
      minute: "2-digit",
    });
  } catch {
    return null;
  }
}

/** Only allow http(s) absolute URLs for external news links. */
function safeHttpUrl(url) {
  if (!url || typeof url !== "string") return null;
  const raw = url.trim();
  if (!raw || raw.length > 2048) return null;
  try {
    const u = new URL(raw);
    if (u.protocol !== "http:" && u.protocol !== "https:") return null;
    if (u.username || u.password) return null;
    return u.toString();
  } catch {
    return null;
  }
}

function NewsItem({ item }) {
  const player = item.player || {};
  const when = formatWhen(item.published_at);
  const extras = (item.players || []).slice(1);
  const safeUrl = safeHttpUrl(item.url);

  return (
    <article
      className="border border-[#0A0A0A] bg-white p-4 sm:p-5 transition-colors hover:bg-[#FAFAFA]"
      data-testid={`news-item-${item.id}`}
    >
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2 flex-wrap min-w-0">
          <span className="text-[10px] uppercase tracking-[0.2em] font-mono-data text-[#52525B]">
            {item.source}
          </span>
          {item.source_kind === "injury" && (
            <span className="text-[10px] uppercase tracking-widest border border-[#0A0A0A]/30 px-1.5 py-0.5 font-mono-data">
              Injury
            </span>
          )}
          {when && (
            <span className="text-[10px] uppercase tracking-[0.15em] font-mono-data text-[#52525B]">
              · {when}
            </span>
          )}
        </div>
        {safeUrl && (
          <a
            href={safeUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest text-[#7C3AED] hover:text-[#0A0A0A] font-semibold"
            data-testid="news-item-link"
          >
            Open <ExternalLink size={12} />
          </a>
        )}
      </div>

      <h3 className="font-display font-bold text-lg sm:text-xl tracking-tight mt-2 leading-snug">
        {item.headline}
      </h3>

      {item.summary && item.summary !== item.headline && (
        <p className="text-sm text-[#52525B] mt-2 leading-relaxed line-clamp-3">{item.summary}</p>
      )}

      <div className="mt-3 flex items-center gap-2 flex-wrap">
        {player.name && (
          <span
            className="inline-flex items-center gap-2 border border-[#0A0A0A]/20 px-2 py-1 text-xs font-mono-data"
            data-testid="news-item-player"
          >
            {player.position && player.position !== "—" && (
              <span className={`text-[9px] font-bold px-1 py-0.5 ${positionColor(player.position)}`}>
                {player.position}
              </span>
            )}
            <span className="font-semibold text-[#0A0A0A]">{player.name}</span>
            {player.team && <span className="text-[#52525B]">{player.team}</span>}
          </span>
        )}
        {extras.map((p) => (
          <span
            key={p.sleeper_id || p.name}
            className="inline-flex items-center gap-1.5 border border-[#0A0A0A]/15 px-2 py-1 text-[11px] font-mono-data text-[#52525B]"
          >
            {p.position && p.position !== "—" ? `${p.position} · ` : ""}
            {p.name}
          </span>
        ))}
      </div>
    </article>
  );
}

export function NewsPanel({
  embedded = false,
  isGuest = false,
  onSignIn,
  user = null,
  selectedLeagueId = null,
  leagues = [],
}) {
  // Platform scope: Sleeper only for now. ESPN/Yahoo roster sync can reuse the same feed UI later.
  const [usernameInput, setUsernameInput] = useState(() => {
    try {
      return localStorage.getItem(NEWS_USER_KEY) || "";
    } catch {
      return "";
    }
  });
  const [activeUsername, setActiveUsername] = useState(() => {
    if (!isGuest && user?.username) return user.username;
    try {
      return localStorage.getItem(NEWS_USER_KEY) || "";
    } catch {
      return "";
    }
  });
  const [leagueFilter, setLeagueFilter] = useState(selectedLeagueId || "");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!isGuest && user?.username) {
      setActiveUsername(user.username);
    }
  }, [isGuest, user?.username]);

  useEffect(() => {
    if (selectedLeagueId) setLeagueFilter(selectedLeagueId);
  }, [selectedLeagueId]);

  const load = useCallback(async () => {
    const signedIn = !isGuest && getUser()?.token;
    if (!signedIn && !activeUsername.trim()) {
      setData(null);
      setError(null);
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const opts = {
        leagueId: leagueFilter || undefined,
        limit: 40,
      };
      const d = signedIn
        ? await fetchMySleeperNews(opts)
        : await fetchPublicSleeperNews(activeUsername.trim(), opts);
      setData(d);
    } catch (e) {
      setData(null);
      setError(apiErrorMessage(e));
    } finally {
      setLoading(false);
    }
  }, [activeUsername, isGuest, leagueFilter]);

  useEffect(() => {
    load();
  }, [load]);

  const leagueOptions = useMemo(() => {
    if (data?.leagues?.length) return data.leagues;
    return leagues || [];
  }, [data?.leagues, leagues]);

  const handleGuestSubmit = (e) => {
    e.preventDefault();
    const u = usernameInput.trim();
    if (!u) return;
    try {
      localStorage.setItem(NEWS_USER_KEY, u);
    } catch {
      /* ignore */
    }
    setActiveUsername(u);
  };

  const items = data?.items || [];

  return (
    <section className={embedded ? "space-y-6" : "mt-0"} data-testid="news-panel">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <span className="label-eyebrow">News · Sleeper owned</span>
          <h2 className="font-display font-black text-3xl sm:text-4xl tracking-tighter mt-1">
            Your players. The headlines.
          </h2>
          <p className="text-sm text-[#52525B] mt-2 max-w-2xl leading-relaxed">
            ESPN NFL headlines matched to players on your Sleeper rosters, plus recent Sleeper injury/status updates.
          </p>
        </div>
        <button
          type="button"
          onClick={load}
          disabled={loading || (!getUser()?.token && !activeUsername.trim())}
          className="brutal-btn-outline px-3 py-2 text-xs uppercase tracking-widest inline-flex items-center gap-2 disabled:opacity-50"
          data-testid="news-refresh"
        >
          <RefreshCw size={14} className={loading ? "animate-spin" : ""} />
          Refresh
        </button>
      </div>

      {isGuest && (
        <form
          onSubmit={handleGuestSubmit}
          className="brutal-card p-4 sm:p-5 flex flex-col sm:flex-row gap-3 sm:items-end"
          data-testid="news-guest-form"
        >
          <div className="flex-1 min-w-0">
            <label className="label-eyebrow" htmlFor="news-sleeper-username">
              Sleeper username
            </label>
            <input
              id="news-sleeper-username"
              value={usernameInput}
              onChange={(e) => setUsernameInput(e.target.value)}
              placeholder="Enter Sleeper username"
              className="mt-2 w-full border border-[#0A0A0A] bg-white px-3 py-2.5 text-sm font-mono-data outline-none focus:bg-[#F3F4F6]"
              data-testid="news-username-input"
              autoComplete="username"
            />
            <p className="text-xs text-[#52525B] mt-2">
              Saved in this browser. Or{" "}
              <button
                type="button"
                onClick={onSignIn}
                className="text-[#7C3AED] font-semibold uppercase tracking-widest text-[10px]"
              >
                sign in
              </button>{" "}
              to use your account.
            </p>
          </div>
          <button
            type="submit"
            className="brutal-btn px-4 py-2.5 text-xs uppercase tracking-widest font-semibold"
            data-testid="news-username-submit"
          >
            Load news
          </button>
        </form>
      )}

      {!isGuest && !user?.username && (
        <SignInRequired
          description="Sign in with your Sleeper username to load news for your owned players."
          onSignIn={onSignIn}
          testId="news-sign-in-required"
        />
      )}

      {(getUser()?.token || activeUsername) && (
        <div className="flex items-center gap-3 flex-wrap">
          <div className="text-xs font-mono-data text-[#52525B]">
            {data?.username || activeUsername || user?.username ? (
              <>
                Tracking · <span className="text-[#0A0A0A]">@{data?.username || activeUsername || user?.username}</span>
                {typeof data?.owned_player_count === "number" && (
                  <> · {data.owned_player_count} owned players</>
                )}
              </>
            ) : null}
          </div>
          {leagueOptions.length > 0 && (
            <select
              value={leagueFilter || ""}
              onChange={(e) => setLeagueFilter(e.target.value)}
              className="px-3 py-2 text-xs uppercase tracking-widest border border-[#0A0A0A] bg-white hover:bg-[#F3F4F6] font-mono-data max-w-[240px]"
              data-testid="news-league-filter"
            >
              <option value="">All Sleeper leagues</option>
              {leagueOptions.map((lg) => (
                <option key={lg.league_id} value={lg.league_id}>
                  {lg.name}
                </option>
              ))}
            </select>
          )}
        </div>
      )}

      {loading && (
        <div
          className="py-20 text-center border border-dashed border-[#0A0A0A]/30"
          data-testid="news-loading"
        >
          <Newspaper size={22} className="mx-auto text-[#52525B] mb-3" />
          <p className="text-sm font-mono-data text-[#52525B]">
            Pulling Sleeper rosters and matching headlines…
          </p>
        </div>
      )}

      {!loading && error && (
        <div className="brutal-card p-6" data-testid="news-error">
          <h3 className="font-display font-black text-xl tracking-tighter">Couldn&apos;t load news.</h3>
          <p className="text-sm text-[#52525B] mt-2">{error}</p>
          <button
            type="button"
            onClick={load}
            className="mt-4 brutal-btn-outline px-3 py-2 text-xs uppercase tracking-widest"
          >
            Try again
          </button>
        </div>
      )}

      {!loading && !error && (getUser()?.token || activeUsername) && items.length === 0 && (
        <div className="py-16 px-6 text-center border border-[#0A0A0A]" data-testid="news-empty">
          <Newspaper size={24} className="mx-auto text-[#52525B] mb-3" />
          <h3 className="font-display font-black text-xl tracking-tighter">No matching headlines right now.</h3>
          <p className="text-sm text-[#52525B] mt-2 max-w-md mx-auto leading-relaxed">
            {data?.owned_player_count
              ? "Your owned players are loaded, but nothing in the latest ESPN feed tagged them. Check back after the next wave of camp or injury notes."
              : "No owned players found for this Sleeper user in the current season. Confirm the username or try another league season."}
          </p>
        </div>
      )}

      {!loading && !error && items.length > 0 && (
        <>
          <div className="space-y-3" data-testid="news-feed">
            {items.map((item) => (
              <NewsItem key={item.id} item={item} />
            ))}
          </div>
          {/* Passive unit below completed feed — never inside item cards or chrome */}
          <AdSlot
            slot={getAdSenseSlot("NEWS")}
            minHeight={100}
            className="mt-6"
            testId="ad-slot-news"
          />
        </>
      )}
    </section>
  );
}
